/**
 * <mb-product-form>: coluna de compra da PDP (portado de components/productPurchase.js).
 * - Marca as combinações de atributos indisponíveis (`-unavailable`) e desabilita as inexistentes (`-disabled`).
 * - Ao trocar a variante: atualiza o input `id` do formulário, o SKU, o preço/parcelas, a flag de desconto,
 *   o botão COMPRAR/ESGOTADO, o "avise-me" e a quantidade máxima; emite `mb:variant:change` (a galeria ouve).
 * - Quantidade: +/− com limite de estoque e as mesmas mensagens do tema anterior.
 * - Envio: POST /cart/add.js, "Adicionado" por 3 s e evento `mb:cart:updated`.
 */
const { emit, updatePriceBlock, updateDiscountFlag, appendUsdPrice } = window.MB;

class MbProductForm extends HTMLElement {
  connectedCallback() {
    this.form = document.getElementById(this.dataset.formId);
    if (!this.form) return;
    this.idInput = this.form.querySelector('[data-variant-id]');
    this.button = this.querySelector('[data-action="add-cart"]');
    // Escopo próprio: o "avise-me" também tem um [data-msg-error] dentro de <mb-product-form>.
    this.error = this.querySelector('[data-purchase-error]');
    this.priceBlock = this.querySelector('[data-price-block]');
    this.flagsBlock = this.querySelector('[data-init-flags]');
    this.skuBlock = this.querySelector('[data-sku-value]');
    this.notify = this.querySelector('[data-form-notify]');
    this.quantityInput = this.querySelector('[name="quantity"]');

    const json = this.querySelector('[data-variants]');
    try {
      this.variants = json ? JSON.parse(json.textContent) : [];
    } catch (error) {
      this.variants = [];
    }

    this.options = Array.from(this.querySelectorAll('[data-prod-option]'));
    this.options.forEach((option) => {
      option.querySelectorAll('input[type="radio"]').forEach((input) => {
        input.addEventListener('change', () => this.onOptionChange());
      });
    });

    this.bindQuantity();
    this.form.addEventListener('submit', (event) => this.addToCart(event));

    if (this.variants.length) {
      this.markAvailability();
      // Publica a variante inicial para a galeria e o "compre junto".
      const variant = this.findVariant(this.selectedValues()) || this.variants[0];
      this.applyVariant(variant, false);
    }
  }

  selectedValues() {
    return this.options.map((option) => {
      const checked = option.querySelector('input[type="radio"]:checked');
      return checked ? checked.value : null;
    });
  }

  findVariant(values) {
    return this.variants.find((variant) => variant.options.every((value, index) => values[index] === null || values[index] === value));
  }

  /**
   * Marca cada valor: `-unavailable` quando a combinação existe mas está sem estoque e
   * `-disabled` quando não existe variante alguma com aquele valor (era o comportamento do markAttributes).
   */
  markAvailability() {
    const selected = this.selectedValues();
    this.options.forEach((option, index) => {
      option.querySelectorAll('label[data-attribute-value]').forEach((label) => {
        const candidate = [...selected];
        candidate[index] = label.dataset.attributeValue;
        const matches = this.variants.filter((variant) => variant.options.every((value, i) => candidate[i] === null || candidate[i] === value));
        label.classList.toggle('-disabled', matches.length === 0);
        label.classList.toggle('-unavailable', matches.length > 0 && !matches.some((variant) => variant.available));
      });
    });
  }

  onOptionChange() {
    this.markAvailability();
    const variant = this.findVariant(this.selectedValues());
    if (!variant) return;
    this.applyVariant(variant, true);
  }

  applyVariant(variant, updateUrl) {
    this.variant = variant;
    if (this.idInput) this.idInput.value = variant.id;
    if (this.skuBlock) this.skuBlock.textContent = variant.sku || this.skuBlock.dataset.skuFallback || '';
    updatePriceBlock(this.priceBlock, variant);
    updateDiscountFlag(this.flagsBlock, variant);
    this.updateButton(variant);
    this.updateNotify(variant);
    this.resetQuantity(variant);
    if (updateUrl && window.history.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }
    emit('mb:variant:change', { productId: this.dataset.productId, variant });
  }

  updateButton(variant) {
    if (!this.button) return;
    const available = Boolean(variant.available);
    this.button.classList.toggle('-unavailable', !available);
    this.button.classList.toggle('-available', available);
    this.button.disabled = !available;
    this.button.textContent = available ? this.button.dataset.textAvailable : this.button.dataset.textUnavailable;
  }

  updateNotify(variant) {
    if (!this.notify) return;
    const show = !variant.available;
    this.notify.classList.toggle('-active', show);
    const sku = this.notify.querySelector('[data-notify-sku]');
    const body = this.notify.querySelector('[data-notify-body]');
    if (sku) sku.value = variant.sku || '';
    if (body && body.dataset.template) body.value = body.dataset.template.replace('[sku]', variant.sku || '');
  }

  resetQuantity(variant) {
    if (!this.quantityInput) return;
    this.quantityInput.value = 1;
    const tracked = variant.inventory_management && variant.inventory_policy === 'deny';
    this.quantityInput.dataset.availableQuantity = tracked ? variant.inventory_quantity : '';
    this.hideError();
  }

  bindQuantity() {
    const plus = this.querySelector('[data-qtd-plus]');
    const minus = this.querySelector('[data-qtd-minus]');
    if (!plus || !minus || !this.quantityInput) return;
    const strings = (window.theme && window.theme.strings) || {};

    plus.addEventListener('click', () => {
      const raw = this.quantityInput.dataset.availableQuantity;
      const max = raw === '' || raw === undefined ? Infinity : Number(raw);
      const next = Number(this.quantityInput.value) + 1;
      if (max === 0) this.showError(strings.outOfStock);
      else if (next > max) this.showError(strings.maxQuantity);
      else {
        this.quantityInput.value = next;
        this.hideError();
      }
    });

    minus.addEventListener('click', () => {
      const next = Number(this.quantityInput.value) - 1;
      if (next > 0) {
        this.quantityInput.value = next;
        this.hideError();
      }
    });
  }

  showError(message) {
    if (!this.error || !message) return;
    this.error.textContent = message;
    this.error.classList.add('-visible');
  }

  hideError() {
    if (!this.error) return;
    this.error.textContent = '';
    this.error.classList.remove('-visible');
  }

  async addToCart(event) {
    event.preventDefault();
    if (!this.button || this.button.disabled) return;
    const formData = new FormData(this.form);
    const variant = this.variants.find((item) => item.id === Number(formData.get('id')));
    appendUsdPrice(formData, variant && variant.price);
    this.button.classList.add('-adding');
    this.hideError();
    try {
      const response = await fetch(window.theme.routes.cartAdd, {
        method: 'POST',
        headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || data.message || 'error');
      this.button.classList.remove('-adding');
      this.button.classList.add('-added');
      const original = this.button.dataset.textAvailable;
      this.button.innerHTML = `<span>${this.button.dataset.textAdded}</span>`;
      emit('mb:cart:updated', { item: data, open: true });
      setTimeout(() => {
        this.button.classList.remove('-added');
        this.button.textContent = original;
      }, 3000);
    } catch (error) {
      this.button.classList.remove('-adding');
      this.showError((window.theme && window.theme.strings && window.theme.strings.addError) || error.message);
    }
  }
}

if (!customElements.get('mb-product-form')) customElements.define('mb-product-form', MbProductForm);
